/**
 * ImageWithSkeleton - Image component with loading skeleton
 * Issue #1323: Onboarding image loading optimization
 *
 * Shows a skeleton placeholder while image loads, with smooth fade-in.
 */

import { useState } from 'react';
import { Skeleton } from './ui/Skeleton';

interface ImageWithSkeletonProps {
  src: string;
  alt: string;
  className?: string;
  skeletonClassName?: string;
  loading?: 'eager' | 'lazy';
  /**
   * CSS `object-position` focal point (#3439). Applied inline so callers can
   * single-source it from data (e.g. onboarding `heroImages.ts`) instead of a
   * hard-coded Tailwind arbitrary value. Paired with an `object-*` fit class.
   */
  objectPosition?: string;
}

export default function ImageWithSkeleton({
  src,
  alt,
  className = '',
  skeletonClassName = '',
  loading = 'eager',
  objectPosition,
}: ImageWithSkeletonProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  return (
    <div className="relative w-full h-full">
      {/* Skeleton shown while loading */}
      {!isLoaded && !hasError && (
        <Skeleton
          variant="image"
          className={`absolute inset-0 w-full h-full ${skeletonClassName}`}
        />
      )}

      {/* Actual image with fade-in */}
      <img
        src={src}
        alt={alt}
        loading={loading}
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        style={objectPosition ? { objectPosition } : undefined}
        className={`${className} transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
}
